import { useQueries, useQuery } from '@tanstack/react-query';
import { get } from '../client';

/**
 * Reuses existing endpoint: GET /api/stores/online/ (StoreOnlineAPIView).
 * Permission: IsAdminOrStaffOrSupportOrOperation.
 * Returns [{ id, name, is_online }] for stores the user can access.
 *
 * Source: src/react_api/views/views.py → StoreOnlineAPIView
 */
export interface StoreLite {
  id: number;
  name: string;
  is_online: boolean;
}

export function useStoresList() {
  return useQuery({
    queryKey: ['stores', 'online'],
    queryFn: () => get<StoreLite[]>('/api/stores/online/'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Per-store roster from `/api/stores/online/{id}/` (`StoreDetailAPIView`
 * in dropit_backend `react_api/views/views.py`).
 *
 * The live payload matches fasto_staff `StoreStaffStatus`: nested
 * `store` plus `riders`, `packers`, `billers`, `managers` arrays of
 * `{ id, name, status, is_online, phone_number?, uses_cycle? }`.
 *
 * We normalize to a flat `StoreDetail` with `*_status_list` keys so
 * BI pages stay unchanged.
 */
export interface StoreStaffRow {
  id: number;
  name: string;
  status: string;
  is_online: boolean;
  phone_number?: string;
  uses_cycle?: boolean;
  [k: string]: unknown;
}

export interface StoreDetail {
  id: number;
  name: string;
  is_online: boolean;
  rider_status_list?: StoreStaffRow[];
  packer_status_list?: StoreStaffRow[];
  biller_status_list?: StoreStaffRow[];
  manager_status_list?: StoreStaffRow[];
  /** Some installs include order counters in the same payload. */
  in_progress_orders?: number;
  pending_orders?: number;
  cancelled_today?: number;
  [k: string]: unknown;
}

function asStaffRows(v: unknown): StoreStaffRow[] {
  if (!Array.isArray(v)) return [];
  return v as StoreStaffRow[];
}

/** Maps `StoreDetailAPIView` / fasto_staff JSON into `StoreDetail`. */
export function normalizeStoreDetailResponse(raw: Record<string, unknown>): StoreDetail {
  const store = raw.store as { id?: number; name?: string; is_online?: boolean } | undefined;

  const id = typeof raw.id === 'number' ? raw.id : store?.id ?? 0;
  const name = typeof raw.name === 'string' ? raw.name : store?.name ?? '';
  const is_online =
    typeof raw.is_online === 'boolean' ? raw.is_online : Boolean(store?.is_online);

  const rider_status_list =
    raw.rider_status_list != null ? asStaffRows(raw.rider_status_list) : asStaffRows(raw.riders);
  const packer_status_list =
    raw.packer_status_list != null ? asStaffRows(raw.packer_status_list) : asStaffRows(raw.packers);
  const biller_status_list =
    raw.biller_status_list != null ? asStaffRows(raw.biller_status_list) : asStaffRows(raw.billers);
  const manager_status_list =
    raw.manager_status_list != null ? asStaffRows(raw.manager_status_list) : asStaffRows(raw.managers);

  return {
    ...raw,
    id,
    name,
    is_online,
    rider_status_list,
    packer_status_list,
    biller_status_list,
    manager_status_list,
  };
}

export function useStoreDetail(storeId: number | null) {
  return useQuery({
    queryKey: ['stores', 'online', storeId],
    queryFn: async () => {
      const raw = await get<Record<string, unknown>>(`/api/stores/online/${storeId}/`);
      return normalizeStoreDetailResponse(raw);
    },
    enabled: storeId != null && storeId > 0,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * Fetch per-store details for many stores at once. Used by the Live Ops
 * "tile per store" mode and the Store Health overview.
 */
export function useStoreDetails(storeIds: number[]) {
  return useQueries({
    queries: storeIds.map((id) => ({
      queryKey: ['stores', 'online', id] as const,
      queryFn: async () => {
        const raw = await get<Record<string, unknown>>(`/api/stores/online/${id}/`);
        return normalizeStoreDetailResponse(raw);
      },
      staleTime: 60_000,
      refetchInterval: 60_000,
    })),
  });
}
